import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice } from '../../utils/currency';

interface PriceDisplayProps {
  /**
   * Price in BGN (primary currency)
   */
  price: number | null;
  
  /**
   * Optional discount price in BGN
   */
  discountPrice?: number;
  
  /**
   * Pre-calculated EUR price (if available from backend)
   */
  priceEur?: number;
  
  /**
   * Pre-calculated EUR discount price (if available from backend)
   */
  discountPriceEur?: number;
  
  /**
   * Size variant for the primary price
   */
  variant?: 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  
  /**
   * Whether to show prices in a single line or stacked
   */
  layout?: 'stacked' | 'inline';
  
  /**
   * Custom styling
   */
  sx?: any;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price,
  discountPrice,
  priceEur,
  discountPriceEur,
  variant = 'h6',
  layout = 'stacked',
  sx,
}) => {
  const { currencySettings, loading } = useSettings();

  // Handle null price or loading settings
  if (price == null || loading) {
    return (
      <Box sx={{ ...sx }}>
        <Typography variant={variant} color="text.secondary">
          {loading ? 'Loading...' : 'Price not available'}
        </Typography>
      </Box>
    );
  }
  
  // Calculate effective price (with discount if applicable)
  const effectivePrice = (discountPrice != null && discountPrice > 0) ? discountPrice : price;
  
  // Determine if there's a discount
  const hasDiscount = discountPrice != null && discountPrice > 0 && discountPrice < price;
  const discountPercentage = hasDiscount ? Math.round(((price - discountPrice) / price) * 100) : 0;
  
  // Format the current price using dynamic settings
  const currentPriceFormatted = formatPrice(effectivePrice, currencySettings);
  const originalPriceFormatted = hasDiscount ? formatPrice(price, currencySettings) : '';

  if (layout === 'inline') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', ...sx }}>
        {/* Main Price Display */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasDiscount && (
            <Typography
              variant="body2"
              sx={{
                textDecoration: 'line-through',
                color: 'text.secondary',
              }}
            >
              {originalPriceFormatted}
            </Typography>
          )}
          <Typography
            variant={variant}
            color={hasDiscount ? 'error' : 'text.primary'}
            sx={{ fontWeight: 'bold' }}
          >
            {currentPriceFormatted}
          </Typography>
        </Box>

        {/* Discount Badge */}
        {hasDiscount && (
          <Chip
            label={`-${discountPercentage}%`}
            size="small"
            color="error"
            sx={{ height: '22px' }}
          />
        )}
      </Box>
    );
  }

  // Stacked layout (default)
  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasDiscount && (
          <Typography
            variant="body2"
            sx={{
              textDecoration: 'line-through',
              color: 'text.secondary',
            }}
          >
            {originalPriceFormatted}
          </Typography>
        )}
        <Typography
          variant={variant}
          color={hasDiscount ? 'error' : 'text.primary'}
          sx={{ fontWeight: 'bold' }}
        >
          {currentPriceFormatted}
        </Typography>
        {hasDiscount && (
          <Chip
            label={`-${discountPercentage}%`}
            size="small"
            color="error"
            sx={{ height: '22px' }}
          />
        )}
      </Box>
    </Box>
  );
};

export default PriceDisplay;